import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { Button, LottiePlayer } from '@repo/design-system-kit';
import { useKycInfo, shouldSkipKyc } from '@repo/due-diligence-kit/kyc';
import { useSentry } from '@repo/shared-kit/sentry';
import { shouldSendToSentry } from '@repo/shared-kit/utils';
import successLottie from '@repo/visual-assets/animations/success.lottie';
import { jsURL } from 'register-partners-js/constants/hosts';
import { fetchNavigationOrganizationsClient as fetchNavigationOrganizations } from 'register-partners-js/utils/fetch-navigation-organizations';
import { StepListWrapper } from '../step-list';
import { AppStoreBadge } from '../app-store-badge';
import { PlayStoreBadge } from '../play-store-badge';
import styles from './registration-next-steps.module.css';

interface RegistrationNextStepsProps {
  isEmbeddedTemplate: boolean;
  isMobile: boolean;
  organizationId: string;
  legalCountry: string;
  partnerName?: string;
}

export function RegistrationNextSteps({
  isEmbeddedTemplate,
  isMobile,
  organizationId,
  legalCountry,
  partnerName,
}: RegistrationNextStepsProps) {
  const { formatMessage } = useIntl();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { data: kycInfo } = useKycInfo(legalCountry);
  const skipKyc = shouldSkipKyc(kycInfo ?? null);
  const sentry = useSentry();

  async function handleRedirectToCards() {
    setIsRedirecting(true);
    try {
      const organizations = await fetchNavigationOrganizations();
      const org = organizations.find(o => o.id === organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }
      window.location.href = `${jsURL}/organizations/${org.slug}`;
    } catch (error) {
      if (shouldSendToSentry(error)) {
        sentry.captureException(error, {
          tags: { component: 'registration-next-steps' },
          extra: { organizationId },
        });
      }

      setIsRedirecting(false);
    }
  }

  const title =
    isEmbeddedTemplate && !skipKyc
      ? formatMessage({ id: 'existing-company.qonto-embed.get-started.title' })
      : formatMessage({ id: 'get-started.existing-company.title' });

  const subtitle = skipKyc
    ? formatMessage(
        { id: 'existing-company.qonto-embed.get-started.subtitle-skip-kyc' },
        { partner: partnerName }
      )
    : formatMessage(
        { id: 'existing-company.qonto-embed.get-started.subtitle' },
        { partner: partnerName }
      );

  const cta =
    isEmbeddedTemplate && !skipKyc
      ? formatMessage({ id: 'existing-company.qonto-embed.get-started.cta' })
      : formatMessage({ id: 'generic.get-started.cta' });

  return (
    <div className={styles.mainContainer}>
      <div className={styles.content}>
        <LottiePlayer
          className={cx('mb-24', styles.icon)}
          data-test-lottie-container
          path={isEmbeddedTemplate && !skipKyc ? '/illustrations/lottie/kyc.json' : successLottie}
        />
        {isMobile ? (
          <>
            <h1 className="heading-section align-center mb-16">
              <FormattedMessage id="get-started.existing-company.congrats.title" />
            </h1>
            <p className="body-standard ml-16 mr-16">
              <FormattedMessage id="get-started.existing-company.congrats.subtitle" />
            </p>
            <p className="body-standard mb-24 ml-16 mr-16">
              <FormattedMessage id="get-started.existing-company.congrats.download-app" />
            </p>
            <AppStoreBadge className={cx('mb-16', styles.badge)} data-test-app-store-btn />
            <PlayStoreBadge className={cx('mb-24', styles.badge)} data-test-play-store-btn />
            <p className="body-standard">
              <FormattedMessage id="get-started.existing-company.congrats.instructions" />
            </p>
          </>
        ) : (
          <>
            <h1 className="heading-section mb-32 align-center" data-test-title>
              {title}
            </h1>
            {isEmbeddedTemplate ? (
              <p className="body-standard ml-16 mr-16 mb-32" data-test-subtitle>
                {subtitle}
              </p>
            ) : (
              <StepListWrapper
                className="mb-8"
                steps={[
                  {
                    title: formatMessage({
                      id: 'get-started.existing-company.list.item-1.title',
                    }),
                    subtitle: formatMessage({
                      id: 'get-started.existing-company.list.item-1.subtitle',
                    }),
                  },
                  ...(skipKyc
                    ? []
                    : [
                        {
                          title: formatMessage({
                            id: 'get-started.existing-company.list.item-2.title',
                          }),
                          subtitle: formatMessage({
                            id: 'get-started.existing-company.list.item-2.subtitle',
                          }),
                        },
                      ]),
                  {
                    title: formatMessage({
                      id: 'get-started.existing-company.list.item-3.title',
                    }),
                  },
                ]}
              />
            )}
            <Button
              data-test-get-started-cta
              isPending={isRedirecting}
              onPress={handleRedirectToCards}
              stretch
              variant="primary"
            >
              {cta}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
